import moment from "moment";
import {PolIcon} from "components/PolIcon/policon";
import React, {useEffect, useState} from "react";
import ReactTooltip from "react-tooltip";
import Confirm from "v4/components/ui/Confirm/Confirm";
import DownloadLink from "v4/components/utils/DownloadLink/DownloadLink";
import ViewLink from "v4/components/utils/ViewLink/ViewLink";
import useDeletePdf from "v4/features/front/generatePdf/hooks/useDeletePdf";
import getDatetimeFormat from "v4/utils/getDatetimeFormat";
import {usePolTranslation} from "v4/hooks/usePolTranslation";
import {useFrontContext} from "v4/contexts/FrontContext";

import './PdfHistory.scss';

/**
 * @param {Array}    pdfFiles      - PDF du devis, déjà filtrés et triés par `versionNumber` décroissant
 * @param {Object}   currentPdf    - PDF courant
 * @param {Function} updateHandler - reçoit `(file, 'remove')` au succès dans la vue show, appelé sans argument dans les listings
 */
export default function PdfHistory({pdfFiles, currentPdf, updateHandler}) {
    const {t} = usePolTranslation();
    const {a11yValues} = useFrontContext();
    const {deletePdf, isLoadingDelete, isFinishedDelete, isErrorDelete} = useDeletePdf(updateHandler);
    const [fileToDelete, setFileToDelete] = useState(null);
    const isEmpty = pdfFiles.length === 0;

    const dateFormat = getDatetimeFormat(true);

    const confirmDelete = () => deletePdf(fileToDelete);

    useEffect(() => {
        if (isFinishedDelete && !isErrorDelete) setFileToDelete(null);
    }, [isFinishedDelete, isErrorDelete]);

    return (
        <div className="pdf-history">
            <table className={`pol-table pdf-history__table ${a11yValues?.contrasted_table ? 'pol-table--contrasted' : ''}`}>
                <thead>
                    <tr>
                        <th data-type="string">{t('version')}</th>
                        <th data-type="string">{t('filename')}</th>
                        <th data-type="string">{t('creation_date')}</th>
                        <th data-type="string">{t('actions')}</th>
                    </tr>
                </thead>
                <tbody>
                    {isEmpty && (
                        <tr>
                            <td className="pol-table__no-results" colSpan={4}>
                                <div>
                                    <PolIcon icon="pdf"/>
                                    {t('no_pdf')}
                                </div>
                            </td>
                        </tr>
                    )}
                    {pdfFiles.map((file) => {
                        const isCurrent = file.id === currentPdf?.id;

                        return (
                            <tr key={file.id} className={isCurrent ? 'is-current' : ''}>
                                <td data-type="string" data-label={t('version')}>
                                    <div className="d-flex justify-content-center">
                                        {isCurrent ? (
                                            <span className="pdf-history__badge">{file.versionNumber} : {t('current_version')}</span>
                                        ) : file.versionNumber}
                                    </div>
                                </td>
                                <td data-type="string" data-label={t('filename')} className="pdf-history__filename">{file.fileLabel}</td>
                                <td data-type="datetime" data-label={t('creation_date')}>{file.createdAt ? moment(file.createdAt).format(dateFormat) : '-'}</td>
                                <td data-type="rowActions" data-label={t('actions')}>
                                    <ul className="table-actions">
                                        {file.downloadLink
                                            ? (
                                                <li data-for="pdf-history-action" data-tip={t('view')}>
                                                    <ViewLink href={file.downloadLink} title="">
                                                        <PolIcon icon="eye"/>
                                                    </ViewLink>
                                                </li>
                                            )
                                            : (
                                                <li data-for="pdf-history-action"
                                                    data-tip={t('link_unavailable')}
                                                    data-disabled="true">
                                                    <PolIcon icon="eye"/>
                                                </li>
                                            )
                                        }
                                        {file.downloadLink
                                            ? (
                                                <li data-for="pdf-history-action" data-tip={t('download')}>
                                                    <DownloadLink fileName={file.fileLabel} href={file.downloadLink} title="">
                                                        <PolIcon icon="download"/>
                                                    </DownloadLink>
                                                </li>
                                            )
                                            : (
                                                <li data-for="pdf-history-action"
                                                    data-tip={t('link_unavailable')}
                                                    data-disabled="true">
                                                    <PolIcon icon="download"/>
                                                </li>
                                            )
                                        }
                                        {file.deleteLink
                                            ? (
                                                <li className="table-actions__delete"
                                                    data-for="pdf-history-action"
                                                    data-tip={t('delete')}
                                                    onClick={() => setFileToDelete(file)}>
                                                    <PolIcon icon="trash"/>
                                                </li>
                                            )
                                            : (
                                                <li className="table-actions__delete"
                                                    data-for="pdf-history-action"
                                                    data-tip={t('link_unavailable')}
                                                    data-disabled="true">
                                                    <PolIcon icon="trash"/>
                                                </li>
                                            )
                                        }
                                    </ul>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            <ReactTooltip id="pdf-history-action" effect="solid"/>

            <Confirm isOpen={fileToDelete !== null}
                     title="delete_pdf"
                     cancelAction={() => setFileToDelete(null)}
                     confirmAction={confirmDelete}
                     isLoading={isLoadingDelete}>
                <p>{t('confirm_delete_pdf')}</p>
            </Confirm>
        </div>
    );
}
